import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, BRL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Clock, LayoutGrid, List } from "lucide-react";
import OpportunityDetail from "@/components/OpportunityDetail";
import { useKanbanTouchDrag, DragGhost } from "@/lib/useKanbanTouchDrag";

const LABEL = { novo_lead:"Novo Lead", contato:"Contato", orcamento:"Orçamento", negociacao:"Negociação", fechado:"Fechado", perdido:"Perdido" };

export default function Funil() {
  const [stages, setStages] = useState([]);
  const [opps, setOpps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dragged, setDragged] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("kanban");
  const [detailId, setDetailId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRef(null);
  const dragRef = useRef({ down: false, x: 0, left: 0 });
  const onDown = (e) => { if (e.target.closest('[draggable="true"]')) return; dragRef.current = { down: true, x: e.pageX, left: scrollRef.current.scrollLeft }; };
  const onMove = (e) => { if (!dragRef.current.down) return; e.preventDefault(); scrollRef.current.scrollLeft = dragRef.current.left - (e.pageX - dragRef.current.x); };
  const onUp = () => { dragRef.current.down = false; };
  const [form, setForm] = useState({ customer_id: "", title: "", product: "", quantity: 15, estimated_value: 0, stage_key: "novo_lead", source: "Manual", notes: "" });

  const load = async () => {
    const [s, o, c] = await Promise.all([api.get("/pipeline/stages"), api.get("/opportunities"), api.get("/customers")]);
    setStages(s.data); setOpps(o.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const cid = searchParams.get("customer");
    if (cid) { setForm((f) => ({ ...f, customer_id: cid })); setOpen(true); setSearchParams({}, { replace: true }); }
  }, [searchParams]);

  const move = async (opp, stage_key) => {
    if (opp.stage_key === stage_key) return;
    await api.patch(`/opportunities/${opp.id}/stage`, { stage_key });
    load();
  };
  const { drag, start: touchStart } = useKanbanTouchDrag(move);
  const create = async () => {
    if (!form.customer_id || !form.title) return toast.error("Cliente e título são obrigatórios");
    if (!Number(form.quantity) || Number(form.quantity) <= 0) return toast.error("Quantidade é obrigatória");
    await api.post("/opportunities", { ...form, quantity: Number(form.quantity)||0, estimated_value: Number(form.estimated_value)||0 });
    toast.success("Oportunidade criada"); setOpen(false); load();
  };

  const daysSince = (iso) => {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  };

  const stageName = (k) => (stages.find((s)=>s.key===k)?.name) || LABEL[k] || k;

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-between shrink-0 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Funil de Vendas</h1>
          <p className="text-slate-400 text-sm mt-1">{view === "kanban" ? "Arraste os cards entre os estágios" : "Todas as oportunidades em lista"}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-[#1E293B] bg-[#0B0F17] p-1" data-testid="funil-view-toggle">
            <button onClick={() => setView("kanban")} data-testid="funil-view-kanban"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view==="kanban" ? "bg-[#0CDBF2] text-[#07090E]" : "text-slate-400 hover:text-white"}`}>
              <LayoutGrid size={14}/> Kanban
            </button>
            <button onClick={() => setView("list")} data-testid="funil-view-list"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view==="list" ? "bg-[#0CDBF2] text-[#07090E]" : "text-slate-400 hover:text-white"}`}>
              <List size={14}/> Lista
            </button>
          </div>
          <Button onClick={() => setOpen(true)} className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="opp-new-btn">
            <Plus size={16} className="mr-2" /> Nova oportunidade
          </Button>
        </div>
      </div>

      {view === "kanban" ? (
        <div ref={scrollRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          className="flex gap-4 overflow-x-auto px-4 sm:px-6 pb-6 flex-1 min-h-0 items-start cursor-grab active:cursor-grabbing select-none snap-x snap-mandatory md:snap-none">
          {stages.map((s) => {
            const items = opps.filter((o) => o.stage_key === s.key);
            const total = items.reduce((a,b) => a + (b.estimated_value||0), 0);
            return (
              <div key={s.key} className={`kanban-col flex-shrink-0 snap-start bg-[#0B0F17] border rounded-xl flex flex-col ${drag?.over===s.key ? "border-[#0CDBF2] ring-2 ring-[#0CDBF2]/60" : "border-[#1E293B]"}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dragged && move(dragged, s.key)}
                data-col-key={s.key}
                data-testid={`kanban-col-${s.key}`}>
                <div className="p-4 border-b border-[#1E293B]">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                    <div className="font-heading font-semibold text-sm">{s.name}</div>
                    <div className="text-xs text-slate-500 ml-auto">{items.length}</div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono">{BRL(total)}</div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {items.map((o) => {
                    const days = daysSince(o.last_interaction);
                    return (
                      <div key={o.id} draggable
                        onDragStart={() => setDragged(o)}
                        onDragEnd={() => setDragged(null)}
                        onTouchStart={(e) => touchStart(e, o, o.title)}
                        onClick={() => setDetailId(o.id)}
                        data-testid={`kanban-card-${o.id}`}
                        style={{ opacity: drag?.item?.id === o.id ? 0.4 : 1, touchAction: "pan-y" }}
                        className="bg-[#111622] border border-[#1E293B] hover:border-[#0CDBF2]/50 rounded-lg p-3 cursor-pointer transition-colors">
                        <div className="font-medium text-sm">{o.title}</div>
                        <div className="text-xs text-slate-400 mt-1">{o.customer_name}{o.customer_company ? " · "+o.customer_company : ""}</div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="text-xs text-[#0CDBF2] font-mono">{BRL(o.estimated_value)}</div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-1"><Clock size={10}/>{days}d</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-6 pb-6">
          <div className="rounded-xl border border-[#1E293B] bg-[#111622] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0B0F17] text-slate-400">
                <tr>{["Título","Cliente","Produto","Qtd","Valor","Estágio","Origem"].map(h=><th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {opps.length === 0 && <tr><td colSpan="7" className="text-center py-12 text-slate-500">Sem oportunidades.</td></tr>}
                {opps.map((o) => (
                  <tr key={o.id} className="border-t border-slate-800/70 cursor-pointer hover:bg-[#0B0F17]" onClick={() => setDetailId(o.id)} data-testid={`opp-row-${o.id}`}>
                    <td className="px-4 py-3 font-medium">{o.title}</td>
                    <td className="px-4 py-3 text-slate-300">{o.customer_name}</td>
                    <td className="px-4 py-3 text-slate-300">{o.product||"—"}</td>
                    <td className="px-4 py-3 text-slate-300">{o.quantity||0}</td>
                    <td className="px-4 py-3 text-[#0CDBF2] font-mono">{BRL(o.estimated_value)}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="border-[#0CDBF2]/40 text-[#0CDBF2]">{stageName(o.stage_key)}</Badge></td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{o.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle>Nova oportunidade</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300 text-sm">Cliente*</Label>
              <select value={form.customer_id} onChange={(e)=>setForm({...form,customer_id:e.target.value})} data-testid="opp-customer"
                className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                <option value="">Selecione</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.company?"— "+c.company:""}</option>)}
              </select></div>
            <div><Label className="text-slate-300 text-sm">Título*</Label>
              <Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-title" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-slate-300 text-sm">Produto</Label>
                <Input value={form.product} onChange={(e)=>setForm({...form,product:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-product"/></div>
              <div><Label className="text-slate-300 text-sm">Qtde*</Label>
                <Input type="number" value={form.quantity} onChange={(e)=>setForm({...form,quantity:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-qty"/></div>
              <div><Label className="text-slate-300 text-sm">Valor R$</Label>
                <Input type="number" value={form.estimated_value} onChange={(e)=>setForm({...form,estimated_value:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-value"/></div>
            </div>
            <div><Label className="text-slate-300 text-sm">Estágio</Label>
              <select value={form.stage_key} onChange={(e)=>setForm({...form,stage_key:e.target.value})} data-testid="opp-stage"
                className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                {stages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select></div>
            <div><Label className="text-slate-300 text-sm">Observações</Label>
              <Textarea value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-notes"/></div>
            <Button onClick={create} className="w-full bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="opp-save">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {detailId && (
        <OpportunityDetail oppId={detailId} stages={stages} onClose={() => setDetailId(null)} onUpdated={load} />
      )}
      <DragGhost drag={drag} />
    </div>
  );
}
